import React from "react";
import { Loader2, KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const FAILURE_COPY = {
  invalid_code: "We don't recognise that invitation code. Check it and try again.",
  expired: "That invitation has expired. Ask your Cruise Command contact for a new one.",
  revoked: "That invitation has been withdrawn.",
  already_used: "That invitation has already been redeemed.",
  wrong_recipient: "That invitation was issued to a different email address.",
  not_yet_redeemable: "That invitation cannot be redeemed yet. Please try again on the start date in your invitation email.",
  too_many_attempts: "Too many attempts. Please wait a few minutes and try again.",
};

export default function AccessInvitation() {
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/access-invitations/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code: code.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setResult(body);
        setCode("");
      } else {
        const failure = body?.detail?.code || "invalid_code";
        setError(FAILURE_COPY[failure] || "We couldn't redeem that invitation.");
      }
    } catch (_e) {
      setError("Network problem — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="access-invitation-page"
      className="min-h-screen w-full bg-[#0b132b] text-white flex items-start justify-center px-6 py-24"
    >
      <div className="w-full max-w-xl">
        <p className="text-[9.5px] font-black tracking-[0.28em] uppercase text-white/40">
          Cruise Command · Early Access
        </p>
        <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight">
          Redeem your Early Access Invitation
        </h1>
        <p className="mt-4 text-sm text-white/60 leading-relaxed">
          Enter your invitation code to activate your access. No payment card is required.
        </p>

        {result ? (
          <div
            data-testid="access-invitation-success"
            className="mt-10 rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.07] p-6"
          >
            <div className="flex items-center gap-2 text-emerald-300">
              <CheckCircle2 className="w-4 h-4" />
              <span data-testid="access-invitation-label" className="text-sm font-semibold">
                {result?.invitation_label || "Early Access Invitation"} confirmed
              </span>
            </div>
            <dl className="mt-4 space-y-1 text-[13px] text-white/70">
              <div className="flex gap-2">
                <dt className="text-white/40">Source</dt>
                <dd data-testid="access-entitlement-source">{result?.entitlement?.source}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-white/40">Expires</dt>
                <dd data-testid="access-entitlement-expires">
                  {result?.entitlement?.expires_at
                    ? new Date(result.entitlement.expires_at).toLocaleDateString()
                    : "—"}
                </dd>
              </div>
            </dl>
            <a
              data-testid="access-invitation-continue"
              href="/"
              className="mt-6 inline-flex items-center rounded-full bg-white px-5 py-2.5 text-[13px] font-semibold text-[#0b132b] transition-colors hover:bg-white/85"
            >
              Enter Cruise Command
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-4">
            <label
              htmlFor="access-code"
              className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45"
            >
              Invitation code
            </label>
            <div className="relative">
              <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                id="access-code"
                data-testid="access-invitation-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                placeholder="CCA-XXXXX-XXXXX-XXXXX"
                className="w-full rounded-xl border border-white/12 bg-white/[0.04] py-3.5 pl-11 pr-4 font-mono text-sm tracking-[0.12em] text-white placeholder:text-white/25 outline-none transition-colors focus:border-white/30"
              />
            </div>

            {error && (
              <div
                data-testid="access-invitation-error"
                className="flex items-start gap-2 rounded-xl border border-amber-400/25 bg-amber-400/[0.07] px-4 py-3 text-[13px] text-amber-200"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid="access-invitation-submit"
              disabled={busy || !code.trim()}
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-[13px] font-semibold text-[#0b132b] transition-colors hover:bg-white/85 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {busy ? "Redeeming…" : "Redeem invitation"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
